import {
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Sheet } from "../phone/Sheet";
import { usePhone } from "../phone/usePhone";
import { cx } from "./cx";

// The Ledger's one dropdown. Replaces every native <select> so the open popup
// is themed (wine accent, custom scrollbar) instead of the OS list. Follows the
// APG select-only combobox pattern: a combobox button + a portalled listbox,
// full keyboard support, aria-activedescendant. Positioning is a body portal so
// the list never clips inside a card or modal.

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}
export interface SelectGroup {
  label: string;
  options: SelectOption[];
}
type SelectOptions = SelectOption[] | SelectGroup[];

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOptions;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
  /** A class on the open list, for a list that should be wider than a small trigger. */
  listClassName?: string;
  /** The option to open at while nothing is chosen, so a long list opens where most people pick. */
  suggested?: string;
}

// ── pure helpers (exported so they can be unit-tested without a DOM) ───────

export function isGrouped(options: SelectOptions): options is SelectGroup[] {
  return options.length > 0 && "options" in options[0];
}

export function flattenOptions(options: SelectOptions): SelectOption[] {
  if (isGrouped(options)) return options.flatMap((g) => g.options);
  return options;
}

export function labelForValue(options: SelectOptions, value: string): string | null {
  const hit = flattenOptions(options).find((o) => o.value === value);
  return hit ? hit.label : null;
}

export function firstEnabledIndex(flat: SelectOption[]): number {
  return flat.findIndex((o) => !o.disabled);
}

export function lastEnabledIndex(flat: SelectOption[]): number {
  for (let i = flat.length - 1; i >= 0; i -= 1) if (!flat[i].disabled) return i;
  return -1;
}

/** Next selectable index in a direction, clamped at the ends (no wrap). */
export function nextEnabledIndex(flat: SelectOption[], from: number, dir: 1 | -1): number {
  for (let i = from + dir; i >= 0 && i < flat.length; i += dir) {
    if (!flat[i].disabled) return i;
  }
  // Nothing further this way: hold the current spot if it's valid, else clamp to an end.
  if (from >= 0 && from < flat.length && !flat[from].disabled) return from;
  return dir === 1 ? lastEnabledIndex(flat) : firstEnabledIndex(flat);
}

/** First selectable option whose label starts with `buffer`, searched from `from`. */
export function typeaheadIndex(flat: SelectOption[], buffer: string, from: number): number {
  const b = buffer.toLowerCase();
  if (!b) return -1;
  const n = flat.length;
  for (let step = 1; step <= n; step += 1) {
    const i = (from + step) % n;
    const o = flat[i];
    if (!o.disabled && o.label.toLowerCase().startsWith(b)) return i;
  }
  // also allow a match at `from` itself (e.g. repeated first letter stays put)
  const cur = flat[from];
  if (cur && !cur.disabled && cur.label.toLowerCase().startsWith(b)) return from;
  return -1;
}

type SelectAction =
  | { kind: "open"; index: number }
  | { kind: "close" }
  | { kind: "highlight"; index: number }
  | { kind: "commit"; index: number }
  | { kind: "none" };

/** Maps a key press to an action. Pure — the component just applies the result. */
export function keyToAction(
  key: string,
  ctx: { open: boolean; highlight: number; flat: SelectOption[]; selectedIndex: number },
): SelectAction {
  const { open, highlight, flat, selectedIndex } = ctx;
  const openAt = selectedIndex >= 0 && !flat[selectedIndex]?.disabled ? selectedIndex : firstEnabledIndex(flat);
  if (!open) {
    if (key === "ArrowDown" || key === "ArrowUp" || key === "Enter" || key === " " || key === "Spacebar") {
      return openAt === -1 ? { kind: "none" } : { kind: "open", index: openAt };
    }
    return { kind: "none" };
  }
  switch (key) {
    case "ArrowDown":
      return { kind: "highlight", index: highlight < 0 ? openAt : nextEnabledIndex(flat, highlight, 1) };
    case "ArrowUp":
      return { kind: "highlight", index: highlight < 0 ? openAt : nextEnabledIndex(flat, highlight, -1) };
    case "Home":
      return { kind: "highlight", index: firstEnabledIndex(flat) };
    case "End":
      return { kind: "highlight", index: lastEnabledIndex(flat) };
    case "Enter":
    case " ":
    case "Spacebar":
      return highlight >= 0 && !flat[highlight]?.disabled ? { kind: "commit", index: highlight } : { kind: "close" };
    case "Escape":
      return { kind: "close" };
    default:
      return { kind: "none" };
  }
}

/** True when `target` is inside none of the given container nodes. */
export function clickedOutside(target: Node | null, containers: Array<Node | null>): boolean {
  if (!target) return true;
  return !containers.some((c) => c != null && c.contains(target));
}

// ── component ──────────────────────────────────────────────────────────────

const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;


interface MenuPos {
  top: number;
  left: number;
  /** The field's own width: the list is never narrower than what it opens from. */
  width: number;
  /** The window, less a margin each side: a long choice wraps to nothing wider. */
  maxWidth: number;
  maxHeight: number;
}

const MAX_MENU_H = 320;
const GAP = 6;
const EDGE = 8;

export function Select({
  value,
  onChange,
  options,
  placeholder = "Select…",
  disabled = false,
  className,
  ariaLabel,
  listClassName,
  suggested,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  // On a phone the choices open in a sheet from the bottom, like the phone's own pickers.
  const phone = usePhone();
  const [sheet, setSheet] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [pos, setPos] = useState<MenuPos | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const typeahead = useRef<{ buffer: string; at: number }>({ buffer: "", at: 0 });

  const baseId = useId();
  const listId = `${baseId}-list`;
  const optionId = (i: number) => `${baseId}-opt-${i}`;

  const flat = useMemo(() => flattenOptions(options), [options]);
  const chosenIndex = useMemo(() => flat.findIndex((o) => o.value === value), [flat, value]);
  const suggestedIndex = useMemo(() => {
    const i = suggested === undefined ? -1 : flat.findIndex((o) => o.value === suggested);
    return i >= 0 && !flat[i].disabled ? i : -1;
  }, [flat, suggested]);
  // Where the list opens and the keys start: the choice, else the suggestion.
  const selectedIndex = chosenIndex >= 0 ? chosenIndex : suggestedIndex;
  const selectedLabel = labelForValue(options, value);
  const grouped = isGrouped(options);

  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const r = trigger.getBoundingClientRect();
    const listH = listRef.current?.scrollHeight ?? MAX_MENU_H;
    const spaceBelow = window.innerHeight - r.bottom - GAP - EDGE;
    const spaceAbove = r.top - GAP - EDGE;
    const desired = Math.min(MAX_MENU_H, listH);
    const placeUp = spaceBelow < desired && spaceAbove > spaceBelow;
    const maxHeight = Math.min(MAX_MENU_H, Math.max(120, placeUp ? spaceAbove : spaceBelow));
    const top = placeUp ? Math.max(EDGE, r.top - GAP - Math.min(desired, maxHeight)) : r.bottom + GAP;
    // The list is as wide as its longest choice ("Every year on September 24"
    // reads in full), so it can be wider than the field it opens from and must
    // be pulled back when that would take it past the window's edge.
    const maxWidth = Math.max(160, window.innerWidth - 2 * EDGE);
    const listW = Math.min(maxWidth, Math.max(r.width, listRef.current?.getBoundingClientRect().width ?? 0));
    const left = Math.max(EDGE, Math.min(r.left, window.innerWidth - EDGE - listW));
    setPos({ top, left, width: r.width, maxWidth, maxHeight });
  }, []);

  useIsoLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => reposition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (clickedOutside(e.target as Node | null, [rootRef.current, listRef.current])) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Once the open list has its height, bring the choice (or the suggestion) to
  // its middle. Why not the effect below: it runs before the list is placed and
  // sized, so a long list opened at its top.
  const centred = useRef(false);
  useIsoLayoutEffect(() => {
    if (!open) {
      centred.current = false;
      return;
    }
    if (centred.current || !pos || highlight < 0) return;
    centred.current = true;
    const list = listRef.current;
    const el = document.getElementById(`${baseId}-opt-${highlight}`);
    if (list && el) list.scrollTop = Math.max(0, el.offsetTop - list.clientHeight / 2 + el.offsetHeight / 2);
  }, [open, pos, highlight, baseId]);

  // Keep the highlighted option in view as the keyboard moves through a long list.
  useEffect(() => {
    if (open && highlight >= 0) {
      document.getElementById(`${baseId}-opt-${highlight}`)?.scrollIntoView({ block: "nearest" });
    }
  }, [open, highlight, baseId]);

  const openMenu = useCallback(
    (index: number) => {
      setHighlight(index);
      setOpen(true);
    },
    [],
  );

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    setPos(null);
    if (refocus) triggerRef.current?.focus();
  }, []);

  const choose = useCallback(
    (v: string) => {
      onChange(v);
      close(true);
    },
    [onChange, close],
  );

  const onKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === "Tab") {
      if (open) close(false);
      return;
    }
    const action = keyToAction(e.key, { open, highlight, flat, selectedIndex });
    if (action.kind !== "none") {
      e.preventDefault();
      if (action.kind === "open") openMenu(action.index);
      else if (action.kind === "highlight") setHighlight(action.index);
      else if (action.kind === "commit") choose(flat[action.index].value);
      else if (action.kind === "close") close(true);
      return;
    }
    // typeahead: printable single character while open
    if (open && e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      const now = Date.now();
      const ta = typeahead.current;
      ta.buffer = now - ta.at > 700 ? e.key : ta.buffer + e.key;
      ta.at = now;
      const from = highlight >= 0 ? highlight : selectedIndex >= 0 ? selectedIndex : 0;
      const hit = typeaheadIndex(flat, ta.buffer, from);
      if (hit >= 0) setHighlight(hit);
    }
  };

  const triggerClick = () => {
    if (disabled) return;
    if (phone) {
      setSheet(true);
      return;
    }
    if (open) close(false);
    else openMenu(selectedIndex >= 0 && !flat[selectedIndex]?.disabled ? selectedIndex : firstEnabledIndex(flat));
  };

  let running = 0;
  const renderOption = (o: SelectOption, index: number) => {
    const isSel = o.value === value;
    const isActive = index === highlight;
    return (
      <div
        key={`${o.value}-${index}`}
        id={optionId(index)}
        role="option"
        aria-selected={isSel}
        aria-disabled={o.disabled || undefined}
        className={cx(
          "fr-select__opt",
          isActive && "is-active",
          isSel && "is-selected",
          o.disabled && "is-disabled",
        )}
        onMouseDown={(e) => e.preventDefault()}
        onMouseEnter={() => {
          if (!o.disabled) setHighlight(index);
        }}
        onClick={() => {
          if (!o.disabled) choose(o.value);
        }}
      >
        <span className="fr-select__opt-lb">{o.label}</span>
        {isSel ? (
          <svg viewBox="0 0 24 24" className="fr-select__check" aria-hidden="true">
            <path d="M5 12l4.5 4.5L19 7" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : null}
      </div>
    );
  };

  const sheetRow = (o: SelectOption) => (
    <button
      key={o.value}
      type="button"
      className="ph-row fr-select__row"
      role="option"
      aria-selected={o.value === value}
      disabled={o.disabled}
      onClick={() => {
        setSheet(false);
        onChange(o.value);
      }}
    >
      <span className="m">
        <span className="t">{o.label}</span>
      </span>
      {o.value === value ? (
        <span className="ph-tick">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 13l4 4 10-10" />
          </svg>
        </span>
      ) : null}
    </button>
  );

  const menuStyle: CSSProperties | undefined = pos
    ? ({
        top: pos.top,
        left: pos.left,
        maxWidth: pos.maxWidth,
        maxHeight: pos.maxHeight,
        "--fr-select-w": `${pos.width}px`,
      } as CSSProperties)
    : { visibility: "hidden" };

  return (
    <div ref={rootRef} className={cx("fr-select", disabled && "is-disabled", className)}>
      <button
        ref={triggerRef}
        type="button"
        className="fr-select__trigger"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={phone ? sheet : open}
        aria-controls={listId}
        aria-activedescendant={open && highlight >= 0 ? optionId(highlight) : undefined}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={triggerClick}
        onKeyDown={onKeyDown}
      >
        <span className={cx("fr-select__value", selectedLabel == null && "is-placeholder")}>
          {selectedLabel ?? placeholder}
        </span>
        <svg viewBox="0 0 24 24" className="fr-select__chev" aria-hidden="true">
          <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open
        ? createPortal(
            <div
              ref={listRef}
              id={listId}
              role="listbox"
              aria-label={ariaLabel}
              className={cx("fr-select__list", listClassName)}
              style={menuStyle}
            >
              {grouped
                ? (options as SelectGroup[]).map((g) => {
                    const start = running;
                    running += g.options.length;
                    return (
                      <div role="group" aria-label={g.label} key={g.label} className="fr-select__grp">
                        <div className="fr-select__group" aria-hidden="true">
                          {g.label}
                        </div>
                        {g.options.map((o, k) => renderOption(o, start + k))}
                      </div>
                    );
                  })
                : (options as SelectOption[]).map((o, k) => renderOption(o, k))}
            </div>,
            document.body,
          )
        : null}

      {phone ? (
        <Sheet open={sheet} onClose={() => setSheet(false)} title={ariaLabel} label={ariaLabel ?? placeholder} tall={flat.length > 8}>
          {grouped ? (
            (options as SelectGroup[]).map((g) => (
              <section key={g.label}>
                <div className="ph-cap">{g.label}</div>
                <div className="ph-grp" role="listbox" aria-label={g.label}>
                  {g.options.map(sheetRow)}
                </div>
              </section>
            ))
          ) : (
            <div className="ph-grp" role="listbox" aria-label={ariaLabel}>
              {(options as SelectOption[]).map(sheetRow)}
            </div>
          )}
        </Sheet>
      ) : null}
    </div>
  );
}
